import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import NativeAppsPanel from '../Apps/NativeAppsPanel';
import { isSuitestreamInstalledApp, normalizeInstalledApps } from '../Remote/installedAppSelection';

const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 10000 });

function AndroidAppPicker({ deviceId, name, onRequestRefresh, onSaved, onClose }) {
  const [apps, setApps] = useState(null);
  const [choices, setChoices] = useState(new Map());
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const alive = useRef(true);
  const reading = useRef(false);
  const writing = useRef(false);

  const read = useCallback(async () => {
    if (reading.current) return;
    reading.current = true;
    try {
      const result = await axios.get(`/api/devices/get-installed-apps?deviceId=${deviceId}`, config());
      if (!result.data?.success) throw new Error('apps_unavailable');
      if (alive.current) { setApps(normalizeInstalledApps(result.data.apps)); setLoadError(''); }
    } catch {
      if (alive.current) setLoadError('Could not retrieve apps. Check the TV connection and refresh the list.');
    } finally { reading.current = false; }
  }, [deviceId]);

  useEffect(() => {
    alive.current = true;
    onRequestRefresh?.();
    void read();
    // Show saved choices immediately, then allow the TV's fresh inventory to
    // arrive. Checkbox overrides survive every read; polling ends after 15 s.
    const timer = window.setInterval(read, 1500);
    const deadline = window.setTimeout(() => window.clearInterval(timer), 15000);
    return () => { alive.current = false; window.clearInterval(timer); window.clearTimeout(deadline); };
  }, [onRequestRefresh, read, refreshVersion]);

  const save = async () => {
    if (!apps?.length || writing.current) return;
    writing.current = true;
    setSaving(true);
    setSaveError('');
    const enabledPackages = apps.filter((app) => choices.has(app.package_name)
      ? choices.get(app.package_name) : app.enabled).map((app) => app.package_name);
    try {
      const result = await axios.post('/api/devices/set-enabled-apps', { deviceId, enabledPackages }, config());
      if (!result.data?.success) throw new Error('selection_not_saved');
      if (alive.current) await onSaved();
    } catch {
      if (alive.current) setSaveError('Could not save app selection. Your choices are still here; try again.');
    } finally { writing.current = false; if (alive.current) setSaving(false); }
  };

  const selectable = (apps || []).filter((app) => app.launchable && !isSuitestreamInstalledApp(app));
  return <section className="dashboard-tv-remote-app-picker" aria-label={`Manage apps for ${name}`}>
    <div className="dashboard-tv-remote-panel-header"><h4>Choose apps to manage</h4></div>
    <p className="dashboard-tv-remote-hint">Select the apps you want in this remote.</p>
    <fieldset disabled={saving} className="dashboard-tv-remote-app-choices">
      <legend className="sr-only">Apps on {name}</legend>
      {apps === null && !loadError && <p>Loading apps…</p>}
      {apps !== null && !selectable.length && <p>No launchable apps reported yet. Refresh the list after installing an app.</p>}
      {selectable.map((app) => <label key={app.package_name} title={app.package_name}>
        <input type="checkbox" checked={choices.has(app.package_name) ? choices.get(app.package_name) : app.enabled}
          onChange={(event) => { const checked = event.target.checked;
            setChoices((previous) => new Map(previous).set(app.package_name, checked)); }} />
        {app.icon_url && <img src={app.icon_url} alt="" />}
        <span>{app.app_name}</span>
      </label>)}
    </fieldset>
    {(loadError || saveError) && <p role="alert">{saveError || loadError}</p>}
    <div className="dashboard-tv-remote-actions">
      <button type="button" className="primary" disabled={!apps?.length || saving} onClick={save}>{saving ? 'Saving…' : 'Save apps'}</button>
      <button type="button" className="secondary" disabled={saving} onClick={() => setRefreshVersion((value) => value + 1)}>Refresh list</button>
      <button type="button" className="secondary" disabled={saving} onClick={onClose}>Cancel</button>
    </div>
  </section>;
}

export default function RemoteAppPicker(props) {
  return props.nativeConnector === 'lg_webos_remote' ? <NativeAppsPanel {...props} /> : <AndroidAppPicker {...props} />;
}
